import React, { useState, useCallback, useEffect, useRef } from 'react';
import { NOTIFICATION_TYPES } from '@vbee-holding/aivoice-shared-lib';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  fetchNotifications,
  fetchGroupsUnreadStatus,
  setActiveGroup,
  markRead,
  markAllRead,
} from '@src/redux/notification/actions';
import NotificationHeader from './NotificationHeader';
import NotificationTabs from './NotificationTabs';
import NotificationList from './NotificationList';
import NotificationDetail from './NotificationDetail';
import { StyledNotificationPanel } from './index.style';

const PANEL_BOTTOM_MARGIN = 46;

const NotificationPanel = ({ anchorEl = null, onClose }) => {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const open = Boolean(anchorEl);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const isInitialized = useRef(false);

  const { activeGroup, unreadStatus, groups } = useSelector(
    (state) => state.notification,
  );
  const currentGroup = groups[activeGroup] || {
    list: [],
    cursor: '',
    hasMore: true,
    loading: false,
  };

  const { list: notifications, cursor, hasMore, loading } = currentGroup;

  useEffect(() => {
    dispatch(fetchGroupsUnreadStatus());
  }, []);

  useEffect(() => {
    if (!open) {
      isInitialized.current = false;
      return;
    }

    if (isInitialized.current) return;

    let targetGroup = activeGroup;

    let maxSendTime = 0;
    let groupWithMaxSendTime = NOTIFICATION_TYPES.PROMOTION;

    Object.entries(groups).forEach(([groupCode, group]) => {
      if (group.list && group.list.length > 0) {
        group.list.forEach((notification) => {
          const sendTime = new Date(notification.sentAt).getTime();
          if (sendTime > maxSendTime) {
            maxSendTime = sendTime;
            groupWithMaxSendTime = groupCode;
          }
        });
      }
    });

    targetGroup = groupWithMaxSendTime;
    dispatch(setActiveGroup(targetGroup));

    dispatch(fetchNotifications({ groupCode: targetGroup }));
    dispatch(markAllRead(targetGroup));

    isInitialized.current = true;
  }, [open, unreadStatus, dispatch, groups, activeGroup]);

  const handleTabChange = useCallback(
    (group) => {
      dispatch(setActiveGroup(group));
      dispatch(markAllRead(group));
      dispatch(fetchNotifications({ groupCode: group }));
    },
    [dispatch],
  );

  const handleLoadMore = useCallback(() => {
    if (!loading && hasMore) {
      dispatch(
        fetchNotifications({
          groupCode: activeGroup,
          cursor,
          isLoadMore: true,
        }),
      );
    }
  }, [dispatch, loading, hasMore, cursor, activeGroup]);

  const handleMarkAllRead = useCallback(() => {
    dispatch(markAllRead());
  }, [dispatch]);

  const handleItemClick = useCallback(
    (notification) => {
      if (!notification.readAt) {
        dispatch(markRead(notification.id));
      }
      const langContent =
        notification.rawContent?.[i18n.language] ||
        notification.rawContent?.vi ||
        {};
      const redirectUrl =
        langContent.additionalType === 'URL' ? langContent.redirectUrl : '';
      if (redirectUrl) {
        window.open(redirectUrl, '_blank', 'noopener,noreferrer');
      } else {
        setSelectedNotification(notification);
      }
    },
    [dispatch, i18n],
  );

  const handleDetailClose = useCallback(() => {
    setSelectedNotification(null);
  }, []);

  return (
    <>
      <StyledNotificationPanel
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        bottom={anchorEl?.getBoundingClientRect()?.bottom + PANEL_BOTTOM_MARGIN}
      >
        <NotificationHeader
          onClose={onClose}
          onMarkAllRead={handleMarkAllRead}
        />
        <NotificationTabs
          activeGroup={activeGroup}
          unreadStatus={unreadStatus}
          onTabChange={handleTabChange}
        />
        <NotificationList
          notifications={notifications}
          loading={loading}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
          onItemClick={handleItemClick}
        />
      </StyledNotificationPanel>

      {selectedNotification && (
        <NotificationDetail
          notification={selectedNotification}
          onClose={handleDetailClose}
        />
      )}
    </>
  );
};

export default NotificationPanel;
